import {
  GTMEventNameEcommerce,
  mapProductGTMItem,
  sendGTMEventEcommerce
} from '@/fo/api/gtm';
import { CartItemInput, cartService } from '@/fo/api/magento';
import { cartMindboxService } from '@/fo/api/mindbox';
import { toastCartAdded } from '@/fo/ui/cart/toast/added';
import { toastBase } from '@/fo/ui/kit/toast/base';
import { MappedProduct } from '@/fo/util/map-product-data';

import { selectCountProductInCart, storeProduct } from '../lib/store';

type FieldProduct =
  | 'sku'
  | 'parentSku'
  | 'name'
  | 'brand'
  | 'series'
  | 'volume'
  | 'basePrice'
  | 'finalPrice'
  | 'itemPrice'
  | 'baseImage';

type InputProduct = Pick<MappedProduct, FieldProduct>;

type InputItemAdd = CartItemInput & {
  product: InputProduct;
};

const getPriceItem = (item: InputProduct) => {
  return item.itemPrice ?? item.finalPrice ?? item.basePrice ?? 0;
};

const addProductsToCart = async (
  cartItems: InputItemAdd[],
  options?: { openInNewTab?: boolean }
) => {
  cartItems.forEach((item) => {
    storeProduct.send({
      type: 'setProductCartLoading',
      sku: item.sku,
      loading: true
    });
  });

  const response = await cartService.addProductsToCart(
    cartItems.map((item) => ({
      sku: item.sku,
      quantity: item.quantity
    }))
  );

  if (response?.errorMessage) {
    toastBase(response.errorMessage, { status: 'error' });
  }

  if (!response?.cart || !response?.items) {
    return;
  }

  response.items.forEach((item) => {
    if (item?.product && item?.product.sku) {
      storeProduct.send({
        type: 'setProduct',
        sku: item?.product.sku,
        cart: {
          id: item?.id ?? '',
          count: item?.quantity ?? 1,
          loading: false,
          isGift: item?.is_cart_rule_gift ?? false
        }
      });
    }
  });

  cartItems.forEach((item) => {
    if (item.sku === item?.product.sku) {
      toastCartAdded({
        src: item.product.baseImage?.src || '',
        subtitle: item?.product.name || '',
        tabs: [item.product.volume || ''],
        openInNewTab: options?.openInNewTab
      });
    }
  });

  const value = cartItems.reduce((acc, item) => {
    return acc + getPriceItem(item.product) * item.quantity;
  }, 0);

  sendGTMEventEcommerce({
    event: 'add_to_cart',
    ecommerce: {
      value: String(value),
      items: cartItems.map((item, index) =>
        mapProductGTMItem(
          { ...item.product, sku: item.sku, quantity: item.quantity },
          index
        )
      )
    }
  });

  const mindboxItems =
    response.items?.map((item) => {
      const product = item?.product;
      const mindboxId =
        product && 'mindbox_id' in product
          ? (product.mindbox_id as string | null | undefined)
          : null;
      return {
        id: mindboxId || '',
        count: item?.quantity || 0,
        price: item?.prices?.price?.value || 0
      };
    }) || [];

  await cartMindboxService.update(mindboxItems);
};

const updateCartItem = async (
  sku: string,
  cartItemId: string,
  quantity: number,
  product: InputProduct
) => {
  storeProduct.send({
    type: 'setProductCartLoading',
    sku,
    loading: true
  });
  const response = await cartService.updateCartItem(cartItemId, quantity);

  if (response?.errorMessage) {
    toastBase(response.errorMessage, { status: 'error' });
  }

  if (!response?.items) {
    return;
  }

  const snapshot = storeProduct.getSnapshot();
  const prevCount = selectCountProductInCart(sku)(snapshot);
  const eventGTM: GTMEventNameEcommerce =
    prevCount > quantity ? 'remove_from_cart' : 'add_to_cart';

  response.items.forEach((item) => {
    const product = item?.product;
    const productSku =
      product && 'sku' in product
        ? (product.sku as string | null | undefined)
        : null;
    if (productSku) {
      storeProduct.send({
        type: 'setProduct',
        sku: productSku,
        cart: {
          id: item?.id ?? '',
          count: item?.quantity ?? 1,
          loading: false,
          isGift: item?.is_cart_rule_gift ?? false
        }
      });
    }
  });

  const priceItem = getPriceItem(product);
  const quantityItem = Math.abs(quantity - prevCount);

  sendGTMEventEcommerce({
    event: eventGTM,
    ecommerce: {
      value: String(priceItem * quantityItem),
      items: [mapProductGTMItem({ ...product, sku, quantity: quantityItem }, 0)]
    }
  });

  const mindboxItems =
    response.items?.map((item) => {
      const product = item?.product;
      const mindboxId =
        product && 'mindbox_id' in product
          ? (product.mindbox_id as string | null | undefined)
          : null;
      return {
        id: mindboxId || '',
        count: item?.quantity || 0,
        price: item?.prices?.price?.value || 0
      };
    }) || [];

  await cartMindboxService.update(mindboxItems);
};

const removeCartItem = async (
  sku: string,
  cartItemId: string,
  product: InputProduct
) => {
  storeProduct.send({
    type: 'setProductCartLoading',
    sku,
    loading: true
  });
  const response = await cartService.removeCartItem(cartItemId);

  if (response?.errorMessage) {
    toastBase(response.errorMessage, { status: 'error' });
  }

  const snapshot = storeProduct.getSnapshot();
  const prevCount = selectCountProductInCart(sku)(snapshot);

  storeProduct.send({
    type: 'setProductCart',
    sku,
    cart: undefined
  });

  const priceItem = getPriceItem(product);

  sendGTMEventEcommerce({
    event: 'remove_from_cart',
    ecommerce: {
      value: String(priceItem * prevCount),
      items: [mapProductGTMItem({ ...product, sku, quantity: prevCount }, 0)]
    }
  });

  const mindboxItems =
    response.items?.map((item) => {
      const product = item?.product;
      const mindboxId =
        product && 'mindbox_id' in product
          ? (product.mindbox_id as string | null | undefined)
          : null;
      return {
        id: mindboxId || '',
        count: item?.quantity || 0,
        price: item?.prices?.price?.value || 0
      };
    }) || [];

  await cartMindboxService.update(mindboxItems);
};

type InputItemRemoveAll = {
  id: string;
  product: InputProduct;
};

const removeAllCartItem = async (cartItems: InputItemRemoveAll[] = []) => {
  const response = await cartService.removeAllCartItem(
    cartItems.map((item) => item.id)
  );

  if (response?.errorMessage) {
    toastBase(response.errorMessage, { status: 'error' });
  }

  const snapshot = storeProduct.getSnapshot();

  storeProduct.send({ type: 'setClearedCart' });

  const gtmItems = cartItems.filter(
    (item) => !snapshot.context.products[item.product.sku]?.cart?.isGift
  );

  if (gtmItems.length === 0) {
    return;
  }

  sendGTMEventEcommerce({
    event: 'remove_from_cart',
    ecommerce: {
      value: String(
        gtmItems.reduce(
          (acc, item) =>
            acc +
            getPriceItem(item.product) *
              selectCountProductInCart(item.product.sku)(snapshot),
          0
        )
      ),
      items: gtmItems.map((item, index) =>
        mapProductGTMItem(
          {
            ...item.product,
            quantity: selectCountProductInCart(item.product.sku)(snapshot)
          },
          index
        )
      )
    }
  });
};

export const cartActions = {
  addProductsToCart,
  updateCartItem,
  removeCartItem,
  removeAllCartItem
};
